import { CommonModule } from '@angular/common';
import { Component, ViewChild, effect, inject, input, output, signal } from '@angular/core';
import { CarouselComponent, CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { RedZoomModule } from 'ngx-red-zoom';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';

export interface Image {
  image: string;
  thumbImage: string;
  alt: string;
  title: string;
  url: string;
}

@Component({
  standalone: true,
  selector: 'base-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
  imports: [CommonModule, CarouselModule, RedZoomModule],
  providers: [PlatformService]
})
export class ImageViewerComponent {
  private platformService = inject(PlatformService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);



  readonly sectionType = input<string>('');

  IMAGE_URLS = IMAGE_URLS;
  imageIndex = 0;

  @ViewChild('previewCarousel') previewCarousel!: CarouselComponent;
  @ViewChild('slideCarousel') slideCarousel!: CarouselComponent;
  isBrowser = this.platformService.isBrowser();

  isDragging: boolean = false;
  startPosition = 0;

  customOptionsSingleImage: OwlOptions = {
    items: 1,
    loop: false,
    margin: 0,
    autoplay: false,
    dots: false,
    nav: false,
    lazyLoad: true,
    responsive: {
      0: {
        items: 1
      },
      600: {
        items: 1
      },
      900: {
        items: 1
      }
    }
  };

  customOptions: OwlOptions = {
    items: 4,
    loop: false,
    margin: 20,
    autoplay: false,
    autoWidth: false,
    dots: false,
    responsive: {
      0: {
        items: 4
      },
      600: {
        items: 4
      },
      900: {
        items: 4
      }
    },
    nav: false
  };

  // Expose device detection service signals for template usage
  get isMobile() { return this.deviceDetectionService.isMobile; }
  get isDesktop() { return this.deviceDetectionService.isDesktop; }
  get isTablet() { return this.deviceDetectionService.isTablet; }
  get deviceType() { return this.deviceDetectionService.deviceType; }



  readonly imagesList = input<string[]>([]);

  get images(): {
    image: string;
    thumbImage: string;
    alt: string;
    title: string;
    widthForSlider: number;
  }[] {
    return this._images;
  }

  private _images: {
    image: string;
    thumbImage: string;
    alt: string;
    title: string;
    widthForSlider: number;
  }[] = [];

  // Update outputs to use Angular 20 output() function
  openImagesInFullScreen$ = output<number>();
  openImagesInFullScreenInMobile$ = output<number>();

  previewImage: string = '';

  // Signal to track when images are ready for carousel
  imagesReady = signal<boolean>(false);

  constructor() {
    // Effect to handle imagesList changes and apply business logic
    effect(() => {
      const images = this.imagesList();

      if (Array.isArray(images) && images.length > 0) {
        // Mark as not ready while processing
        this.imagesReady.set(false);

        this._images = images.map((url: string) => ({
          image: url,
          thumbImage: url,
          alt: '',
          title: '',
          widthForSlider: 120
        }));

        this.previewImage = this._images?.length ? this._images[0]?.image || '' : '';

        if (images.length <= (this.customOptions.items || 4)) {
          this.customOptions = {
            ...this.customOptions,
            autoWidth: true,
            margin: 10
          };
        }

        // Use setTimeout with sufficient delay for carousel initialization
        // Combined with CSS fade-in animation for smooth appearance
        setTimeout(() => {
          this.imagesReady.set(true);
        }, 100);
      } else {
        // when no image is set
        this._images = [];
        this.imagesReady.set(false);
      }
    });
  }

  get classes(): string {
    return 'red-zoom--style--window red-zoom--mode--square red-zoom-magnifier' + (this.isDragging ? ' red-zoom--disabled' : '');
  }

  setCarouselStartPosition() {
    // Guard clause - ensure carousels are initialized
    if (!this.slideCarousel?.slidesOutputData || !this.previewCarousel?.slidesOutputData) {
      return;
    }

    const sliderStartPosition = this.slideCarousel.slidesOutputData.startPosition;
    const previewStartPosition = this.previewCarousel.slidesOutputData.startPosition;
    const numberOfItemsInSlider = this.customOptions.items;

    // Additional guard to ensure all values are defined
    if (sliderStartPosition === undefined || previewStartPosition === undefined || numberOfItemsInSlider === undefined) {
      return;
    }

    const isInRange = this.getIsInRange({ sliderStartPosition, previewStartPosition, numberOfItemsInSlider });

    if (!isInRange) {
      if (previewStartPosition > sliderStartPosition) {
        this.slideCarousel.to(this.previewCarousel.slidesOutputData.startPosition + '');
      } else {
        let targetPosition = sliderStartPosition - numberOfItemsInSlider;

        // handle edge case when target becomes -ve
        targetPosition = targetPosition > 0 ? targetPosition : 0;
        this.slideCarousel.to(targetPosition + '');
      }
    }
  }

  openImagesInFullScreen() {
    if (this.previewCarousel.slidesOutputData?.startPosition !== undefined) {
      this.openImagesInFullScreen$.emit(this.previewCarousel.slidesOutputData.startPosition);
    }
  }

  openImagesInFullScreenInMobile() {
    if (this.previewCarousel.slidesOutputData?.startPosition !== undefined) {
      this.openImagesInFullScreenInMobile$.emit(this.previewCarousel.slidesOutputData.startPosition);
    }
  }

  seeChangedIndex(event: any) {
    this.imageIndex = event.startPosition;
  }

  private getIsInRange({ sliderStartPosition, previewStartPosition, numberOfItemsInSlider }: {
    sliderStartPosition: number | undefined;
    previewStartPosition: number | undefined;
    numberOfItemsInSlider: number | undefined;
  }): boolean {
    if (!sliderStartPosition || !previewStartPosition || !numberOfItemsInSlider) {
      return false;
    }
    return (
      previewStartPosition >= sliderStartPosition &&
      previewStartPosition <= sliderStartPosition +
      (numberOfItemsInSlider - 1)
    );
  }
}

